import { cn } from "@/shared/lib/utils";
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

export interface Breadcrumb {
  label: string;
  to?: string;
}

interface PageHeaderProps {
  title: string;
  description?: string | undefined;
  breadcrumbs?: Breadcrumb[] | undefined;
  actions?: ReactNode;
  className?: string | undefined;
  testId?: string | undefined;
}

/** Hlavička stránky — breadcrumbs + nadpis + volitelný popisek + slot pro akce vpravo. */
export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  className,
  testId = "page-header",
}: PageHeaderProps) {
  return (
    <header className={cn("mb-6 flex flex-col gap-3", className)} data-testid={testId}>
      {breadcrumbs && breadcrumbs.length > 0 ? (
        <nav aria-label="Drobečková navigace" data-testid={`${testId}-breadcrumbs`}>
          <ol className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
            {breadcrumbs.map((crumb, idx) => {
              const isLast = idx === breadcrumbs.length - 1;
              return (
                <li key={`${crumb.label}-${idx}`} className="flex items-center gap-1">
                  {idx > 0 ? (
                    <ChevronRight className="h-3 w-3 shrink-0" aria-hidden="true" />
                  ) : null}
                  {crumb.to && !isLast ? (
                    <Link
                      to={crumb.to}
                      className="hover:text-foreground hover:underline"
                      data-testid={`${testId}-breadcrumb-${idx}`}
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span
                      className={cn(isLast && "font-medium text-foreground")}
                      aria-current={isLast ? "page" : undefined}
                    >
                      {crumb.label}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      ) : null}

      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <h1
            className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl"
            data-testid={`${testId}-title`}
          >
            {title}
          </h1>
          {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 items-center gap-2" data-testid={`${testId}-actions`}>
            {actions}
          </div>
        ) : null}
      </div>
    </header>
  );
}
